/**
 * TodayAtAGlance — Section 1 of the Office Manager Report.
 *
 * Displays real-time session counts, payment totals, and operational alerts.
 * data-testid: candeza-reports-operations-glance-*
 */

import React from 'react';
import styled from 'styled-components';
import type { TodaySnapshot, SessionBreakdown } from '@/api/reports';

// ── Styled components ─────────────────────────────────────────────────────────

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
  gap: ${({ theme }) => theme.spacing.md};
`;

const KpiCard = styled.div`
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  padding: ${({ theme }) => theme.spacing.md};
`;

const KpiLabel = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSizes.xs};
  color: ${({ theme }) => theme.colors.textSecondary};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: ${({ theme }) => theme.spacing.xs};
`;

const KpiValue = styled.div<{ $color?: string }>`
  font-size: ${({ theme }) => theme.typography.fontSizes['2xl']};
  font-weight: ${({ theme }) => theme.typography.fontWeights.bold};
  color: ${({ $color, theme }) => $color ?? theme.colors.text};
`;

const KpiSub = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSizes.xs};
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-top: 2px;
`;

const SessionRow = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.sm};
  flex-wrap: wrap;
  margin-top: ${({ theme }) => theme.spacing.xs};
`;

const SessionChip = styled.span<{ $color: string }>`
  font-size: ${({ theme }) => theme.typography.fontSizes.xs};
  background: ${({ $color }) => $color}22;
  color: ${({ $color }) => $color};
  border-radius: 4px;
  padding: 1px 6px;
`;

// ── Component ─────────────────────────────────────────────────────────────────

interface TodayAtAGlanceProps {
  snapshot: TodaySnapshot;
  loading?: boolean;
}

function SessionsDetail({ s }: { s: SessionBreakdown }) {
  return (
    <SessionRow>
      {s.confirmed > 0 && <SessionChip $color="#3b82f6">{s.confirmed} scheduled</SessionChip>}
      {s.in_progress > 0 && <SessionChip $color="#10b981">{s.in_progress} in progress</SessionChip>}
      {s.completed > 0 && <SessionChip $color="#6b7280">{s.completed} done</SessionChip>}
      {s.cancelled > 0 && <SessionChip $color="#ef4444">{s.cancelled} cancelled</SessionChip>}
    </SessionRow>
  );
}

export const TodayAtAGlance: React.FC<TodayAtAGlanceProps> = ({ snapshot, loading }) => {
  if (loading) {
    return (
      <div data-testid="candeza-reports-operations-glance-loading">Loading glance metrics…</div>
    );
  }

  return (
    <Grid data-testid="candeza-reports-operations-glance-grid">
      {/* Sessions */}
      <KpiCard data-testid="candeza-reports-operations-glance-sessions">
        <KpiLabel>Sessions Today</KpiLabel>
        <KpiValue $color="#3b82f6">{snapshot.sessions.total}</KpiValue>
        <SessionsDetail s={snapshot.sessions} />
      </KpiCard>

      {/* Bookings */}
      <KpiCard data-testid="candeza-reports-operations-glance-bookings">
        <KpiLabel>New Bookings</KpiLabel>
        <KpiValue>{snapshot.bookings_created_today}</KpiValue>
      </KpiCard>

      {/* Payments */}
      <KpiCard data-testid="candeza-reports-operations-glance-payments">
        <KpiLabel>Payments</KpiLabel>
        <KpiValue $color="#10b981">{snapshot.payments_today_count}</KpiValue>
        {snapshot.payments_today_total > 0 && (
          <KpiSub>₹{snapshot.payments_today_total.toLocaleString('en-IN')}</KpiSub>
        )}
      </KpiCard>

      {/* New students */}
      <KpiCard data-testid="candeza-reports-operations-glance-new-students">
        <KpiLabel>New Students</KpiLabel>
        <KpiValue>{snapshot.new_students_today}</KpiValue>
      </KpiCard>

      {/* Trial inquiries */}
      <KpiCard data-testid="candeza-reports-operations-glance-trials">
        <KpiLabel>Trial Inquiries</KpiLabel>
        <KpiValue $color={snapshot.trial_inquiries_pending > 0 ? '#f97316' : undefined}>
          {snapshot.trial_inquiries_pending}
        </KpiValue>
        <KpiSub>pending confirmation</KpiSub>
      </KpiCard>

      {/* Awaiting attendance */}
      <KpiCard data-testid="candeza-reports-operations-glance-attendance">
        <KpiLabel>Awaiting Attendance</KpiLabel>
        <KpiValue $color={snapshot.sessions_awaiting_attendance > 0 ? '#ef4444' : undefined}>
          {snapshot.sessions_awaiting_attendance}
        </KpiValue>
        <KpiSub>sessions overdue</KpiSub>
      </KpiCard>
    </Grid>
  );
};

